import React from "react";
import PropTypes from "prop-types";
import {
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Grid,
  Link,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import "./EMPInteractiveList.css";
import { TransitionGroup, CSSTransition } from "react-transition-group";

// available props
// hasListAction: bool,
// listActionText: string,
// listActionOnClick: function,
// primaryActionOnClick: function,
// secondaryActionOnClick: function,
// items: array of objects
// taskName: string,
// taskDetails: string,
// hasActions: bool,
// primaryActionText: string,
// secondaryActionText: string,

const blackUnderlinedLinkSx = {
  "&&": {
    color: "#000 !important",
    fontFamily: 'var(--body-1-regular-font-family, "Ford F-1")',
    fontSize: "var(--body-1-regular-font-size, 16px)",
    fontStyle: "normal",
    fontWeight: "var(--body-1-regular-font-weight, 400)",
    lineHeight: "var(--body-1-regular-line-height, 24px)",
    textDecorationLine: "underline !important",
    textDecorationStyle: "solid",
    textDecorationSkipInk: "auto",
    textDecorationThickness: "auto",
    textUnderlineOffset: "auto",
    textUnderlinePosition: "from-font",
  },

  "&&:link, &&:visited, &&:hover, &&:active, &&:focus, &&:focus-visible": {
    color: "#000 !important",
    textDecorationLine: "underline !important",
  },
};

const useStyles = makeStyles({
  buttonRoot: {
    justifyContent: "center",
    border: "1px solid #9C9C9C",
    borderRadius: 8,
    marginBottom: "16px",
    padding: "30px 16px",
    color: "#000",

    "&:hover": {
      backgroundColor: "#fff",
      color: "#000",
    },
  },

  root: {
    display: "flex",
    border: "1px solid #9C9C9C",
    borderRadius: 8,
    justifyContent: "space-between",
    alignItems: "stretch",
    padding: 16,
    marginBottom: "16px",
  },

  container: {
    display: "flex",
    border: "1px solid #9C9C9C",
    borderRadius: 8,
    marginBottom: "16px",
    padding: 16,
  },

  secondaryAction: {
    flex: "0 1 auto",
    margin: 0,
    padding: 0,
    border: "none",
  },

  secondaryActionRoot: {
    position: "relative",
    top: "unset",
    left: "unset",
    right: "unset",
    transform: "none",
    flex: "0 0 auto",
    color: "#000",
  },

  textRoot: {
    margin: 0,
  },

  body1: {
    fontSize: "14px",
    lineHeight: "20px",
    color: "inherit",
  },

  multiline: {
    margin: 0,
  },

  primary: {
    color: "var(--neutrals-gray-gray-64-d-4-d-4-d, #4D4D4D)",
    fontFamily: 'var(--subtitle-regular-font-family, "Ford F-1")',
    fontSize: "var(--subtitle-regular-font-size, 20px)",
    fontStyle: "normal",
    fontWeight: "var(--subtitle-regular-font-weight, 400)",
    lineHeight: "var(--subtitle-regular-line-height, 28px)",
  },

  secondary: {
    fontSize: "14px",
    lineHeight: "24px",
    color: "#4D4D4D",
    marginTop: "8px",
  },
});

const EMPInteractiveList = ({
  hasListAction,
  listActionText,
  listActionOnClick,
  primaryActionOnClick,
  secondaryActionOnClick,
  items,
  disableListAction,
  hasActions,
  primaryActionText,
  secondaryActionText,
  testId,
  appear,
}) => {
  const classes = useStyles();

  const listItems = Array.isArray(items) ? items : [];

  return (
    <>
      {hasListAction && (
        <ListItem
          classes={{ root: classes.buttonRoot }}
          disabled={disableListAction}
          onClick={listActionOnClick}
        >
          <Link
            data-testid="list-action-button"
            component="button"
            variant="body1"
            color="inherit"
            underline="always"
            sx={blackUnderlinedLinkSx}
          >
            {listActionText}
          </Link>
        </ListItem>
      )}

      <TransitionGroup component={List} data-testid={testId} appear={appear}>
        {listItems.map(({ id, taskName, taskDescription, taskUrl }) => (
          <CSSTransition
            key={id}
            timeout={appear ? 500 : 0}
            classNames={appear ? "item" : ""}
            appear={appear}
          >
            <ListItem
              data-testid="interactive-list-item"
              classes={{
                root: classes.root,
                container: classes.container,
                secondaryAction: classes.secondaryAction,
              }}
            >
              <ListItemText
                primary={taskName}
                secondary={taskDescription}
                classes={{
                  root: classes.textRoot,
                  primary: classes.primary,
                  secondary: classes.secondary,
                  multiline: classes.multiline,
                }}
              />

              {hasActions && (
                <ListItemSecondaryAction
                  classes={{ root: classes.secondaryActionRoot }}
                >
                  <Grid container spacing={2} justifyContent="flex-end">
                    {primaryActionText && (
                      <Grid item>
                        <Link
                          data-testid="primary-action-link"
                          component="button"
                          variant="body1"
                          color="inherit"
                          underline="always"
                          sx={blackUnderlinedLinkSx}
                          onClick={() =>
                            primaryActionOnClick?.({
                              id,
                              taskName,
                              taskDescription,
                              taskUrl,
                            })
                          }
                        >
                          {primaryActionText}
                        </Link>
                      </Grid>
                    )}

                    {secondaryActionText && (
                      <Grid item>
                        <Link
                          data-testid="secondary-action-link"
                          component="button"
                          variant="body1"
                          color="inherit"
                          underline="always"
                          sx={blackUnderlinedLinkSx}
                          onClick={() => secondaryActionOnClick?.(id)}
                        >
                          {secondaryActionText}
                        </Link>
                      </Grid>
                    )}
                  </Grid>
                </ListItemSecondaryAction>
              )}
            </ListItem>
          </CSSTransition>
        ))}
      </TransitionGroup>
    </>
  );
};

EMPInteractiveList.propTypes = {
  hasListAction: PropTypes.bool,
  listActionText: PropTypes.string,
  listActionOnClick: PropTypes.func,
  primaryActionOnClick: PropTypes.func,
  secondaryActionOnClick: PropTypes.func,
  items: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      taskName: PropTypes.string,
      taskDescription: PropTypes.string,
      taskUrl: PropTypes.string,
    })
  ),
  disableListAction: PropTypes.bool,
  hasActions: PropTypes.bool,
  primaryActionText: PropTypes.string,
  secondaryActionText: PropTypes.string,
  testId: PropTypes.string,
  appear: PropTypes.bool,
};

EMPInteractiveList.defaultProps = {
  hasListAction: false,
  listActionText: "",
  listActionOnClick: undefined,
  primaryActionOnClick: undefined,
  secondaryActionOnClick: undefined,
  items: [],
  disableListAction: false,
  hasActions: false,
  primaryActionText: "",
  secondaryActionText: "",
  testId: undefined,
  appear: false,
};

export default EMPInteractiveList;
